import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageMarker from 'react-image-marker';

import { ReactComponent as CarIcon } from '../../../assets/icons/black-car-icon.svg';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close-icon.svg';
import ComingSoonImages from '../../../assets/icons/coming-soon.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil-card.svg';
import PumpIcon from '../../../assets/icons/gas-icon.svg';
import { ReactComponent as ImageBreakIcon } from '../../../assets/icons/image-break-icon.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import BackgroundImage from '../../../assets/images/background.png';
import NoDataFound from '../../../assets/images/no-data-found.png';
import { useAppBranding } from '../../../config/branding';
import { useGoogleMaps } from '../../../contexts/google-map-context/GoogleMapContent';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
import stationManagement from '../../../utils/api/station-management';
import DeviceSearch from '../components/DeviceSearch';

import AddLaneStation from './AddLaneStation';
import EditLaneStation from './EditLaneStation';

interface ChargerStatus {
  status: string;
  count: number;
}

interface MarkerWithDevice {
  top: number;
  left: number;
  device: string;
  chargingPointSn: string;
  position: {
    top: number;
    left: number;
  };
}

interface LaneData {
  siteMapUrl: string;
  markers: MarkerWithDevice[];
  issues?: boolean;
}

interface Device {
  id: string;
  name: string;
  status: 'available' | 'maintenance' | 'error' | 'unavailable';
  connectorType?: string;
  connectors?: any[];
  latitude?: number;
  longitude?: number;
  chargingPointSn: string;
}

/**
 * Check the Image Url for valid
 * @param {string} url The image URL.
 * @returns {boolean} Whether the URL is valid.
 */
const isValidUrl = (url?: string): boolean => {
  if (!url) return false;
  try {
    return Boolean(new URL(url));
  } catch {
    return false;
  }
};

/**
 * check the lat and lng is valid or not
 */
const validatePosition = (position: { latitude: any; longitude: any }) => {
  const lat = parseFloat(position.latitude);
  const lng = parseFloat(position.longitude);
  return !Number.isNaN(lat) && !Number.isNaN(lng) ? { lat, lng } : null;
};

/**
 * Station Devices Lane Component
 * @returns {React.JSX.Element} The Station Devices Lane component.
 */
function StationDevicesLane() {
  const branding = useAppBranding();
  const { t } = useTranslation('stationManagement');
  const { isLoaded } = useGoogleMaps();
  const mapReference = useRef<google.maps.Map | null>(null);
  const [activeTab, setActiveTab] = useState<string>('mapLane');
  const [openTab, setOpenTab] = useState<number>(0);
  const [devices, setDevices] = useState<Device[]>([]);
  const dispatch = useAppDispatch();
  const [chargerStatus, setChargerStatus] = useState<ChargerStatus[]>([]);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const [lanes, setLanes] = useState<LaneData[]>([]);
  const { stationDetails } = useAppSelector((state) => state.stationDetails);
  const [loading, setLoading] = useState(false);
  const [loadedMaps, setLoadedMaps] = useState<Record<string, boolean>>({});

  /**
   * Fetch device list
   */
  const handleDevicesList = async () => {
    if (!selectedChargingPoint) return;

    try {
      const response = await stationManagement.fetchDeviceList({
        chargingStation: selectedChargingPoint?.id,
      });

      const formattedDevices: Device[] = response.data.map((device: any) => ({
        id: device._id,
        name: device.chargingPointName || 'Empty',
        chargingPointSn: device.chargingPointSn,
        status: device.isDeviceOffline ? 'unavailable' : 'available',
        connectorType: device.connectorType,
        connectors: device.connectors,
      }));

      setDevices(formattedDevices);
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        },
      });
    }
  };

  /**
   * Fetch lane map list
   */
  const handleLaneMapList = async () => {
    if (!selectedChargingPoint?.id) return;
    setLoading(true);
    try {
      const response = await stationManagement.fetchLaneMapList({
        chargingStation: selectedChargingPoint?.id,
      });
      const dataArray = Array.isArray(response.data) ? response.data : [response.data];

      const laneData: LaneData[] = dataArray.map((item: any) => ({
        siteMapUrl: item.chargingStationMap?.[0]?.signedUrl || '',
        markers: item.chargingPointLocations.map((loc: any) => ({
          top: loc.position.top,
          left: loc.position.left,
          device: loc.chargingPointSn,
          chargingPointSn: loc.chargingPointSn,
          position: { top: loc.position.top, left: loc.position.left },
        })),
      }));

      setLanes(laneData);
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message,
          },
        },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLanes([]);
    if (selectedChargingPoint?.id) {
      handleDevicesList();
      handleLaneMapList();
    }
  }, [selectedChargingPoint]);

  /**
   *  active tab section
   * @param {number} number The tab index.
   */
  const changeTabPages = (number: number) => {
    setOpenTab(number);
  };

  /**
   * Image handle error
   * @param {string} url The image URL.
   */
  const handleImageLoad = (url: string) => {
    setLoadedMaps((previous) => ({
      ...previous,
      [url]: true,
    }));
  };

  /**
   * Connector Status
   */
  const handleConnectorStatus = async () => {
    try {
      const response = await stationManagement.fetchConnectorStatus({
        chargingStation: selectedChargingPoint?.id,
      });
      setChargerStatus(response?.data?.statusCounts);
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        },
      });
    }
  };

  useEffect(() => {
    handleConnectorStatus();
  }, [selectedChargingPoint]);

  /**
   * Render Lane Toggle Button
   */
  const renderLaneToggleButton = () => {
    switch (activeTab) {
      case 'mapLane':
        return (
          <div
            className="add-lane"
            role="button"
            tabIndex={0}
            onClick={() => setActiveTab('defaultLane')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') setActiveTab('defaultLane');
            }}>
            <span>{t('defaultLane')}</span>
          </div>
        );

      case 'defaultLane':
        return (
          <div
            className="add-lane"
            role="button"
            tabIndex={0}
            onClick={() => setActiveTab('mapLane')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') setActiveTab('mapLane');
            }}>
            <span>{t('mapLane')}</span>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <DeviceSearch />
      <div className="station-devices-lane-content">
        <div className="station-heading">
          <div className="table-tab">
            <CustomToggleButton
              defaultActiveIndex={openTab}
              onToggle={changeTabPages}
              labels={[t('lane'), t('cctv'), t('map')]}
            />
          </div>

          {lanes.length > 0 ? (
            <>
              {openTab !== 1 && openTab !== 2 && renderLaneToggleButton()}
              <div
                className={`add-lane ${activeTab === 'editLane' ? 'active' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => setActiveTab('editLane')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setActiveTab('editLane');
                  }
                }}>
                <EditIcon style={{ width: '20px' }} />
                <span>{t('editLane')}</span>
              </div>
            </>
          ) : (
            <div
              className="add-lane"
              role="button"
              tabIndex={0}
              onClick={() => setActiveTab('addLane')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setActiveTab('addLane');
                }
              }}>
              <PlusIcon style={{ width: '15px' }} /> <span>{t('addLane')}</span>
            </div>
          )}
        </div>

        {openTab === 0 && (
          <>
            {(() => {
              if (loading) {
                return (
                  <div className="skeleton-grid-loader">
                    <div className="skeleton-card" />
                  </div>
                );
              }

              if (lanes.length > 0) {
                return (
                  <>
                    {(activeTab === 'mapLane' ||
                      activeTab === 'addLane' ||
                      activeTab === 'editLane') && (
                      <div className="lane-display">
                        {lanes.map((lane) => (
                          <div key={lane.siteMapUrl}>
                            <img
                              src={lane.siteMapUrl}
                              alt="preload"
                              style={{ display: 'none' }}
                              onLoad={() => handleImageLoad(lane.siteMapUrl)}
                            />
                            {!loadedMaps[lane.siteMapUrl] ? (
                              <div className="error-message">
                                <ImageBreakIcon />
                                <span>{t('laneMapError')}</span>
                                <div>
                                  <CloseIcon />
                                </div>
                              </div>
                            ) : null}
                            <ImageMarker
                              src={
                                isValidUrl(lane.siteMapUrl) && loadedMaps[lane.siteMapUrl]
                                  ? lane.siteMapUrl
                                  : BackgroundImage
                              }
                              markers={lane.markers}
                              markerComponent={(marker) => {
                                const m = marker as unknown as MarkerWithDevice;
                                return (
                                  <div className="marker-wrapper">
                                    <div className="device-marker">
                                      <img
                                        src={PumpIcon}
                                        alt="Pump"
                                        className="device-details-image"
                                      />
                                      <p className="marker-device-label">
                                        {devices.find(
                                          (d) => d.chargingPointSn === m.chargingPointSn,
                                        )?.name || m.device}
                                      </p>
                                    </div>
                                    <div className="device-status-container online">
                                      <div className="static-container" />
                                      <div className="animation-container" />
                                    </div>
                                  </div>
                                );
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    {activeTab === 'defaultLane' && (
                      <div className="device-page-car">
                        <div className="device-grid">
                          {devices.map((device) => (
                            <div key={device.id} className={`device-card ${device.status}`}>
                              {device.status === 'available' && (
                                <div className="car-image">
                                  <CarIcon />
                                </div>
                              )}
                              <div className="device-info">
                                {device.status === 'maintenance' && (
                                  <p className="status-text">{t('maintenance')}</p>
                                )}
                                {device.status === 'error' && (
                                  <p className="status-text">{t('error')}</p>
                                )}
                                {device.status === 'unavailable' && (
                                  <p className="status-text unavailable-text">{t('unavailable')}</p>
                                )}
                                <div
                                  className={`device-name ${
                                    device.name.length > 25 ? 'long' : ''
                                  }`}>
                                  {device.name}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              }

              return (
                <div className="table-no-data-wrap">
                  <img src={NoDataFound} alt="" className="no-data-found-image" />
                  <div className="no-data-found-details">
                    <h3>{t('nothingHereYet')}</h3>
                    <p>{t('noDataDescCharging')}</p>
                  </div>
                </div>
              );
            })()}

            {/* Status Labels */}
            <div className="devices-status-label">
              <div className="each-label">
                <p>{t('charging')}</p>
                <div className="each-label-color" style={{ backgroundColor: '#2AA700' }}>
                  {chargerStatus.find((s) => s.status === 'Charging')?.count || 0}
                </div>
              </div>
              <div className="each-label">
                <p>{t('finishing')}</p>
                <div className="each-label-color" style={{ backgroundColor: '#A7A7A7' }}>
                  {chargerStatus.find((s) => s.status === 'Finishing')?.count || 0}
                </div>
              </div>
              <div className="each-label">
                <p>{t('preparing')}</p>
                <div className="each-label-color" style={{ backgroundColor: '#F79009' }}>
                  {chargerStatus.find((s) => s.status === 'Preparing')?.count || 0}
                </div>
              </div>
              <div className="each-label">
                <p>{t('error')}</p>
                <div className="each-label-color" style={{ backgroundColor: '#E41010' }}>
                  {chargerStatus.find((s) => s.status === 'Faulted')?.count || 0}
                </div>
              </div>
            </div>
          </>
        )}

        {/* CCTV Tab */}
        {openTab === 1 && (
          <div className="cctv-container">
            <center>
              <img src={ComingSoonImages} alt="Coming soon" />
            </center>
          </div>
        )}

        {/* Map Tab */}
        {openTab === 2 && (
          <div className="map-container">
            <div className="map">
              {isLoaded && stationDetails ? (
                <GoogleMap
                  mapContainerStyle={{
                    width: '100%',
                    height: '100%',
                  }}
                  center={{
                    lat: Number(stationDetails?.latitude),
                    lng: Number(stationDetails?.longitude),
                  }}
                  zoom={3}
                  onLoad={(map) => {
                    mapReference.current = map; // Assign the map instance to the reference
                  }}>
                  {stationDetails &&
                    (() => {
                      const validPosition = validatePosition({
                        latitude: stationDetails.latitude,
                        longitude: stationDetails.longitude,
                      });
                      return validPosition ? (
                        <Marker
                          key="01"
                          position={validPosition}
                          // clusterer={clusterer}
                          icon={{
                            url: branding.icons.stationLocator,
                            scaledSize: new window.google.maps.Size(44, 44), // Width, Height
                          }}
                        />
                      ) : null;
                    })()}
                </GoogleMap>
              ) : null}
            </div>

            <div className="available-status-label">
              <div className="each-label">
                <div className="each-label-color" style={{ backgroundColor: '#2AA700' }} />
                <p>{t('available')}</p>
              </div>
              <div className="each-label">
                <div className="each-label-color" style={{ backgroundColor: '#A7A7A7' }} />
                <p>{t('unavailable')}</p>
              </div>
              <div className="each-label">
                <div className="each-label-color" style={{ backgroundColor: '#F79009' }} />
                <p>{t('maintenance')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Lane Panels */}
        <div
          className={`addLane-pages ${
            activeTab === 'addLane' || activeTab === 'editLane' ? '' : 'close'
          }`}>
          {activeTab === 'addLane' && (
            <AddLaneStation
              onClose={() => {
                setActiveTab('mapLane');
              }}
              handleLaneMapList={handleLaneMapList}
            />
          )}

          {activeTab === 'editLane' && lanes.length > 0 && (
            <EditLaneStation
              onClose={() => {
                setActiveTab('mapLane');
              }}
              initialPreview={lanes[0].siteMapUrl}
              initialMarkers={lanes[0].markers}
              handleLaneMapList={handleLaneMapList}
            />
          )}
        </div>
      </div>
    </>
  );
}

export default StationDevicesLane;
